/**
 * 구성원 목록
 * ---------------------------------------------------------------------------
 * 암호화된 이름에 대한 검색·정렬 제약을 화면에서 어떻게 다루는지 보여주는 기준 화면이다.
 * (ADR-0007)
 *
 *   가나다 정렬 : 서버가 복호화 후 Intl.Collator('ko-KR') 로 정렬한다.
 *                 테이블 헤더의 정렬은 서버 정렬을 트리거할 뿐, 클라이언트에서
 *                 페이지 내부만 다시 정렬하지 않는다(전체 순서가 깨진다).
 *   이름 검색   : n-gram 인덱스 기반. 전방일치/포함/초성을 지원한다.
 *   이메일·전화 : 완전일치만 가능하므로 검색 입력에서 제외했다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  DatePicker,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  HighlightOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  LinkOutlined,
  ReloadOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  StopOutlined,
  UnlockOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText, useInvalidateUsers } from '../../lib/queries';
import { hasPermission, useAuth } from '../../lib/auth-store';
import { CodeTag, useCodes } from '../../lib/codes';
import { downloadCsv } from '../../lib/csv';
import UserFormModal, { type UserFormValue } from './UserFormModal';
import MemberBulkModal from './MemberBulkModal';
import UserImageModal from './UserImageModal';
import UserAccessRuleModal from './UserAccessRuleModal';

const { Text } = Typography;

interface DirectoryItem {
  userId: string;
  empNo: string | null;
  name: string;
  email: string;
  phone: string | null;
  /** 수정 화면의 '부서' 초기값. 이 칸이 없어서 부서가 늘 비어 보였다. */
  departmentId: string | null;
  departmentName: string | null;
  positionCode: string | null;
  employmentType: string | null;
  hireYm: string | null;
  leaveYm: string | null;
  birthMd: string | null;
  status: string;
  lastLoginAt: string | null;
  /** 이 콘솔에서의 역할. 비어 있으면 관리 기능이 열리지 않는다. */
  roleIds: string[];
  roleNames: string[];
  /** 제품 안에서의 역할. 예: { crm: 'TeamLeader' } */
  productRoles: Record<string, string>;
  /** 청구서·세금계산서를 받기로 지정된 사람인가 */
  billingRecipient: boolean;
}

interface PagedResult {
  items: DirectoryItem[];
  page: number;
  size: number;
  total: number;
  hasNext: boolean;
}

type SearchMode = 'contains' | 'prefix';

/** 구성원 상태의 표시명과 색은 공통코드(USER_STATUS)가 정한다. */
const CODE_GROUPS = ['USER_STATUS', 'POSITION'];

export default function UserListPage() {
  const codes = useCodes(CODE_GROUPS);
  const { profile } = useAuth();
  const { message } = AntdApp.useApp();
  const invalidate = useInvalidateUsers();
  const client = useQueryClient();

  const canWrite = hasPermission(profile, 'platform.user:write');
  const canDelete = hasPermission(profile, 'platform.user:delete');
  /*
   * 누가 청구서를 받는지는 **회사 돈에 관한 결정**이라 과금 권한을 본다.
   * 구성원을 고칠 수 있다고 해서 청구서 수신처까지 바꿀 수 있으면 안 된다.
   * 서버도 BILLING_MANAGE 로 다시 판정한다.
   */
  const canBilling = hasPermission(profile, 'platform.billing:manage');
  const myUserId = profile?.userId ?? null;

  const [bulkOpen, setBulkOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserFormValue | null>(null);
  const [imageTarget, setImageTarget] = useState<DirectoryItem | null>(null);
  /* 접속 제한(허용 IP). 운영자 계정처럼 전 고객사에 닿는 계정에 건다. */
  const [accessTarget, setAccessTarget] = useState<DirectoryItem | null>(null);

  const [keyword, setKeyword] = useState('');
  const [submittedKeyword, setSubmittedKeyword] = useState('');
  const [mode, setMode] = useState<SearchMode>('contains');
  const [status, setStatus] = useState<string | undefined>();
  const [sort, setSort] = useState('name:asc');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);

  const query = useQuery<PagedResult, NormalizedError>({
    queryKey: ['users', submittedKeyword, mode, status, sort, page, size],
    queryFn: async () => {
      const { data } = await apiClient.get<PagedResult>('/users', {
        params: {
          keyword: submittedKeyword || undefined,
          prefixOnly: mode === 'prefix',
          status,
          sort,
          page,
          size,
        },
      });
      return data;
    },
  });

  /**
   * 청구서·세금계산서를 받을 사람 지정.
   *
   * 한 명이라도 고르면 그 사람들만 받고, 아무도 안 고르면 소유자·관리자가
   * 받는다. 그 상태가 바뀌는 순간을 알려 주어야 운영 담당자가 놀라지 않는다.
   */
  const billingRecipient = useMutation<
    { explicit: boolean; recipients: Array<{ name: string; email: string }> },
    NormalizedError,
    { userId: string; on: boolean }
  >({
    mutationFn: async (v) =>
      (await apiClient.patch(`/billing/document-recipients/${v.userId}`, { on: v.on })).data,
    onSuccess: (r, v) => {
      message.success(
        v.on
          ? '이 구성원이 청구서·세금계산서를 받습니다.'
          : r.explicit
            ? '수신자에서 뺐습니다.'
            : '지정된 수신자가 없어, 이제 소유자·관리자가 받습니다.',
      );
      void client.invalidateQueries({ queryKey: ['users'] });
      void client.invalidateQueries({ queryKey: ['billing', 'document-recipients'] });
    },
    onError: (e) => message.error(errorText(e)),
  });

  /*
   * 지금 누가 받는지. 표의 스위치가 전부 꺼져 있어도 **아무도 못 받는 것이
   * 아니다** — 지정이 없으면 소유자·관리자가 받는다. 그 사실을 표 위에
   * 적어 두지 않으면 담당자가 스위치를 켜야 하는 줄 안다.
   */
  const recipients = useQuery<
    { explicit: boolean; recipients: Array<{ name: string; email: string }> },
    NormalizedError
  >({
    queryKey: ['billing', 'document-recipients'],
    queryFn: async () => (await apiClient.get('/billing/document-recipients')).data,
    enabled: hasPermission(profile, 'platform.billing:read'),
    retry: false,
  });

  const runSearch = () => {
    setSubmittedKeyword(keyword.trim());
    setPage(1);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<DirectoryItem> | SorterResult<DirectoryItem>[],
  ) => {
    setPage(pagination.current ?? 1);
    setSize(pagination.pageSize ?? 50);

    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    if (single?.field && single.order) {
      setSort(`${String(single.field)}:${single.order === 'ascend' ? 'asc' : 'desc'}`);
    } else {
      setSort('name:asc');
    }
  };

  const columns: ColumnsType<DirectoryItem> = [
    {
      title: '사번',
      dataIndex: 'empNo',
      key: 'empNo',
      width: 110,
      sorter: true,
      render: (value: string | null) => value ?? <Text type="secondary">-</Text>,
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      width: 140,
      // 서버가 가나다 정렬을 수행한다. 기본 정렬이므로 defaultSortOrder 를 지정한다.
      sorter: true,
      defaultSortOrder: 'ascend',
      render: (value: string) => <strong>{value}</strong>,
    },
    { title: '부서', dataIndex: 'departmentName', key: 'departmentName', width: 180 },
    { title: '이메일', dataIndex: 'email', key: 'email', width: 220, ellipsis: true },
    {
      title: '연락처',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (value: string | null) =>
        value ? value.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3') : '-',
    },
    {
      title: '입사',
      dataIndex: 'hireYm',
      key: 'hireYm',
      width: 100,
      sorter: true,
      render: (value: string | null) =>
        value ? `${value.slice(0, 4)}.${value.slice(4, 6)}` : '-',
    },
    {
      /*
       * 이 콘솔에서 무엇을 할 수 있는지. 비어 있으면 로그인은 되지만 관리
       * 기능은 아무것도 열리지 않는다 — 그 상태가 눈에 보여야 "왜 403 이 나지"
       * 를 헤매지 않는다.
       */
      title: 'WORK&Hub 권한',
      dataIndex: 'roleNames',
      key: 'roleNames',
      width: 170,
      render: (value: string[] | undefined) =>
        value && value.length > 0 ? (
          <Space size={4} wrap>
            {value.map((n) => (
              <Tag key={n}>{n}</Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">없음</Text>
        ),
    },
    {
      /* 제품 안에서의 역할. 지금은 CRM 만 쓴다. */
      title: 'CRM 권한',
      dataIndex: 'productRoles',
      key: 'productRoles',
      width: 130,
      render: (value: Record<string, string> | undefined) =>
        value?.crm ? <Tag color="blue">{value.crm}</Tag> : <Text type="secondary">기본값</Text>,
    },
    {
      /*
        청구서·세금계산서를 받을 사람.

        ## 왜 구성원 목록에 두나
        받아야 하는 사람은 보통 **경리 담당자**인데, 그 사람이 Hub 관리자가
        아닌 경우가 흔하다. 사람을 보고 정하는 일이라 사람 목록에 둔다.

        ## 아무도 안 고르면
        소유자·관리자가 받는다(지금까지와 같다). 그 상태를 표 위의 안내로
        알려 준다 — 여기 스위치가 전부 꺼져 있다고 해서 **아무도 못 받는
        것이 아니다.**
      */
      title: (
        <Tooltip title="청구서·세금계산서를 메일로 받을 사람. 아무도 고르지 않으면 소유자·관리자가 받습니다.">
          청구 문서 수신
        </Tooltip>
      ),
      key: 'billingRecipient',
      width: 110,
      align: 'center',
      render: (_, row) => (
        <Switch
          size="small"
          checked={row.billingRecipient}
          disabled={!canBilling || billingRecipient.isPending}
          onChange={(on) => billingRecipient.mutate({ userId: row.userId, on })}
        />
      ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (value: string) => <CodeTag codes={codes} group="USER_STATUS" value={value} />,
    },
    {
      title: '관리',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          {/*
            운영일지 결재란에 찍히는 서명이다. 본인은 언제나, 남의 것은
            구성원 관리 권한이 있을 때만 — 서버가 다시 판정한다.
          */}
          <Tooltip title="사진 · 서명">
            <Button
              size="small"
              type="text"
              icon={<HighlightOutlined />}
              disabled={!canWrite && row.userId !== myUserId}
              onClick={() => setImageTarget(row)}
            />
          </Tooltip>
          <Tooltip title="수정">
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              disabled={!canWrite}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          {/*
            메일을 받을 수 없는 주소로 등록된 계정은 초대 메일이 닿지 않아
            영영 들어올 수 없다. 링크를 직접 꺼내 전달할 수 있게 한다.
            초대 중인 계정에만 보인다.
          */}
          {row.status === 'INVITED' && (
            <Tooltip title="초대 링크 발급">
              <Button
                size="small"
                type="text"
                icon={<LinkOutlined />}
                disabled={!canWrite}
                onClick={() => issueInviteLink(row)}
              />
            </Tooltip>
          )}
          <Tooltip title="접속 제한 (허용 IP)">
            <Button
              size="small"
              type="text"
              icon={<SafetyCertificateOutlined />}
              disabled={!canWrite}
              onClick={() => setAccessTarget(row)}
            />
          </Tooltip>
          {/*
            로그인 중지 / 해제.
            계정을 지우지 않고 들어오지 못하게만 한다. 휴직·사고 대응처럼
            되돌릴 일이 잦은 자리라 삭제와 분리해 둔다.
          */}
          <Tooltip title={row.status === 'SUSPENDED' ? '로그인 중지 해제' : '로그인 중지'}>
            <Button
              size="small"
              type="text"
              icon={row.status === 'SUSPENDED' ? <UnlockOutlined /> : <StopOutlined />}
              disabled={!canWrite || row.userId === myUserId || row.status === 'LEFT'}
              onClick={() => confirmSuspend(row)}
            />
          </Tooltip>
          {/*
            퇴사 처리. 퇴사일을 적으면 구성원 상태가 '퇴사' 가 되고
            재직자 목록·좌석 수에서 빠진다. 기록은 남는다.
          */}
          <Tooltip title="퇴사 처리">
            <Button
              size="small"
              type="text"
              icon={<LogoutOutlined />}
              disabled={!canWrite || row.userId === myUserId || Boolean(row.leaveYm)}
              onClick={() => confirmLeave(row)}
            />
          </Tooltip>
          <Tooltip title="삭제">
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              onClick={() => confirmDelete(row)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  /**
   * 초대 링크를 새로 발급해 보여 준다.
   *
   * 링크 자체가 비밀번호를 정할 수 있는 열쇠다. 화면에 띄워만 주고,
   * 전달은 관리자가 안전한 경로로 한다.
   */
  async function issueInviteLink(row: DirectoryItem) {
    try {
      const { data } = await apiClient.post<{ link: string; expiresInDays: number }>(
        `/users/${row.userId}/invite-link`,
      );

      Modal.info({
        title: `초대 링크 — ${row.name}`,
        width: 640,
        content: (
          <Space direction="vertical" size="small" style={{ display: 'flex', marginTop: 12 }}>
            <Text>
              이 링크로 본인이 비밀번호를 정하면 로그인할 수 있습니다.
              <b> {data.expiresInDays}일</b> 동안 유효하며, 한 번 쓰면 사라집니다.
            </Text>
            <Input.TextArea value={data.link} readOnly autoSize />
            <Text type="warning">
              링크를 아는 사람은 누구나 이 계정의 비밀번호를 정할 수 있습니다. 메신저·메일 등
              안전한 경로로 본인에게만 전달하세요.
            </Text>
          </Space>
        ),
        okText: '닫기',
      });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  }

  function openEdit(row: DirectoryItem) {
    setEditTarget({
      userId: row.userId,
      email: row.email,
      name: row.name,
      phone: row.phone,
      empNo: row.empNo,
      positionCode: row.positionCode,
      /*
       * 부서·고용형태도 넣는다.
       *
       * 서버는 처음부터 둘 다 내려주고 있었는데 여기서 옮겨 담지 않아,
       * 수정 창을 열면 **부서가 늘 '부서 선택'** 으로 비어 있었다.
       * 그대로 저장하면 지정해 둔 부서가 지워졌다.
       */
      departmentId: row.departmentId,
      employmentType: row.employmentType ?? undefined,
      // 지금 가진 권한을 수정 화면의 초기값으로 넣는다. 빠뜨리면 저장할 때
      // 빈 값으로 덮어써서, 이름만 고쳤는데 권한이 사라진다.
      roleIds: row.roleIds ?? [],
      productRoles: row.productRoles ?? {},
      /*
       * 날짜는 목록에 연월·월일만 있어 여기서는 비워 둔다.
       * 수정 창이 열리면서 상세를 받아 채운다(UserFormModal).
       */
      hireDate: null,
      birthDate: null,
    });
    setFormOpen(true);
  }

  /**
   * 로그인 중지 / 해제.
   *
   * 계정을 지우지 않고 들어오지 못하게만 한다. 되돌릴 수 있어야 하는 자리라
   * 삭제와 분리한다 — 삭제는 사유를 받고 기록을 남기는 한 방향 동작이다.
   */
  function confirmSuspend(row: DirectoryItem) {
    const suspended = row.status === 'SUSPENDED';

    Modal.confirm({
      title: suspended
        ? `${row.name} 님의 로그인 중지를 해제할까요?`
        : `${row.name} 님의 로그인을 중지할까요?`,
      content: (
        <Text type="secondary">
          {suspended
            ? '다시 로그인할 수 있게 됩니다.'
            : '계정은 그대로 두고 로그인만 막습니다. 자료와 기록은 남고, 언제든 해제할 수 있습니다.'}
        </Text>
      ),
      okText: suspended ? '중지 해제' : '로그인 중지',
      okButtonProps: { danger: !suspended },
      cancelText: '취소',
      async onOk() {
        try {
          await apiClient.patch(`/users/${row.userId}`, {
            status: suspended ? 'ACTIVE' : 'SUSPENDED',
          });
          message.success(suspended ? '로그인 중지를 해제했습니다.' : '로그인을 중지했습니다.');
          invalidate();
        } catch (err) {
          message.error(errorText(err as NormalizedError));
          return Promise.reject(err);
        }
      },
    });
  }

  /**
   * 퇴사 처리.
   *
   * 퇴사일을 적으면 서버가 구성원 상태를 '퇴사' 로 바꾼다. 재직자 목록과
   * 좌석 수에서 빠지지만 작성한 자료와 기록은 그대로 남는다 — 지난 일지의
   * 작성자가 사라지면 법정 서식이 성립하지 않는다.
   */
  function confirmLeave(row: DirectoryItem) {
    let leaveDate = dayjs().format('YYYY-MM-DD');

    Modal.confirm({
      title: `${row.name} 님을 퇴사 처리할까요?`,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            재직자 목록과 좌석 수에서 빠집니다. 작성한 자료와 기록은 그대로 남습니다.
          </Text>
          <DatePicker
            style={{ width: '100%' }}
            defaultValue={dayjs()}
            allowClear={false}
            placeholder="퇴사일"
            onChange={(d) => {
              leaveDate = d ? d.format('YYYY-MM-DD') : '';
            }}
          />
        </Space>
      ),
      okText: '퇴사 처리',
      okButtonProps: { danger: true },
      cancelText: '취소',
      async onOk() {
        if (!leaveDate) {
          message.error('퇴사일을 선택해 주세요.');
          return Promise.reject(new Error('leaveDate required'));
        }
        try {
          await apiClient.patch(`/users/${row.userId}`, { leaveDate });
          message.success(`${row.name} 님을 퇴사 처리했습니다.`);
          invalidate();
        } catch (err) {
          message.error(errorText(err as NormalizedError));
          return Promise.reject(err);
        }
      },
    });
  }

  function confirmDelete(row: DirectoryItem) {
    let reason = '';
    Modal.confirm({
      title: `${row.name} 님을 삭제하시겠습니까?`,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            계정이 비활성화되고 로그인할 수 없게 됩니다. 감사 추적을 위해 기록은 보존됩니다.
          </Text>
          <Input
            placeholder="삭제 사유 (필수)"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </Space>
      ),
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      async onOk() {
        if (!reason.trim()) {
          message.error('삭제 사유를 입력해 주세요.');
          return Promise.reject(new Error('reason required'));
        }
        try {
          await apiClient.delete(`/users/${row.userId}`, { data: { reason: reason.trim() } });
          message.success(`${row.name} 님을 삭제했습니다.`);
          invalidate();
        } catch (err) {
          message.error(errorText(err as NormalizedError));
          throw err;
        }
      },
    });
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space wrap size="middle">
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={runSearch}
            placeholder="이름 검색 (초성 가능: ㅎㄱㄷ)"
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 260 }}
          />

          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} optionType="button">
            <Radio.Button value="contains">포함</Radio.Button>
            <Radio.Button value="prefix">앞글자</Radio.Button>
          </Radio.Group>

          <Select
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            allowClear
            placeholder="상태"
            style={{ width: 120 }}
            options={codes.options('USER_STATUS')}
          />

          <Button type="primary" onClick={runSearch} loading={query.isFetching}>
            검색
          </Button>

          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} />

          {/*
            구성원 명부를 파일로 받는다. 서비스를 그만두더라도 조직 자료는
            가져갈 수 있어야 한다(클라우드컴퓨팅법 제27조의 이용자 정보 반환).

            ⚠ 지금 보고 있는 **한 페이지**만 받는다. 목록이 페이지로 끊겨
            오기 때문이다. 단추에 건수를 적어 두어, 전체를 받은 줄 알고
            끝내는 일이 없게 한다.
          */}
          <Button
            icon={<DownloadOutlined />}
            disabled={(query.data?.items ?? []).length === 0}
            onClick={() =>
              downloadCsv('구성원', query.data?.items ?? [], [
                { header: '사번', value: (r) => r.empNo },
                { header: '이름', value: (r) => r.name },
                { header: '이메일', value: (r) => r.email },
                { header: '연락처', value: (r) => r.phone },
                { header: '부서', value: (r) => r.departmentName },
                { header: '직위', value: (r) => codes.label('POSITION', r.positionCode) },
                { header: '입사', value: (r) => r.hireYm },
                { header: '퇴사', value: (r) => r.leaveYm },
                { header: '상태', value: (r) => codes.label('USER_STATUS', r.status) },
                { header: 'Hub 권한', value: (r) => r.roleNames.join(' / ') },
                {
                  header: '제품 권한',
                  value: (r) =>
                    Object.entries(r.productRoles ?? {})
                      .map(([product, role]) => `${product}:${role}`)
                      .join(' / '),
                },
                { header: '최근 로그인', value: (r) => r.lastLoginAt },
              ])
            }
          >
            엑셀 내려받기
            {query.data?.items?.length ? ` (${query.data.items.length}명)` : ''}
          </Button>

          {/*
            일괄등록. 「구성원 등록」 바로 왼쪽에 둔다 — 한 명 넣으려다
            여러 명인 걸 깨닫는 순서라, 찾아 헤매지 않게 나란히 둔다.
          */}
          <Button
            icon={<UploadOutlined />}
            disabled={!canWrite}
            onClick={() => setBulkOpen(true)}
          >
            엑셀 일괄등록
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canWrite}
            onClick={() => {
              setEditTarget(null);
              setFormOpen(true);
            }}
          >
            구성원 등록
          </Button>

          <Tooltip
            title={
              '이름은 암호화 저장되므로 부분검색은 전용 색인으로, 가나다 정렬은 서버에서 처리됩니다. ' +
              '이메일·연락처는 완전일치 조회만 가능합니다.'
            }
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      </Card>

      {query.isError && (
        <Alert type="error" showIcon message={query.error.message} />
      )}

      {/*
        「청구 문서 수신」 스위치가 전부 꺼져 있어도 아무도 못 받는 것이
        아니다 — 지정이 없으면 소유자·관리자가 받는다. 지금 누가 받는지를
        이름으로 적어 둔다.
      */}
      {recipients.data && (
        <Alert
          type={recipients.data.explicit ? 'success' : 'info'}
          showIcon
          message={
            recipients.data.explicit
              ? '청구서·세금계산서는 지정하신 분들이 받습니다'
              : '청구서·세금계산서는 소유자·관리자가 받습니다 (지정된 수신자 없음)'
          }
          description={
            <>
              지금 받는 분:{' '}
              {recipients.data.recipients.length > 0 ? (
                <b>
                  {recipients.data.recipients
                    .map((r) => `${r.name || r.email}`)
                    .join(' · ')}
                </b>
              ) : (
                <Text type="danger">없음 — 청구서 메일이 아무에게도 가지 않습니다</Text>
              )}
              {!recipients.data.explicit && (
                <>
                  <br />
                  경리 담당자처럼 따로 받을 분이 있으면 아래 표의{' '}
                  <b>「청구 문서 수신」</b> 을 켜 주세요. 한 분이라도 켜면 그분들만
                  받습니다.
                </>
              )}
            </>
          }
        />
      )}

      <Table<DirectoryItem>
        rowKey="userId"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isLoading}
        size="middle"
        onChange={handleTableChange}
        pagination={{
          current: query.data?.page ?? page,
          pageSize: query.data?.size ?? size,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (total) => `총 ${total.toLocaleString()}명`,
        }}
        /* 「청구 문서 수신」 칸이 늘었다 */
        scroll={{ x: 1220 }}
        locale={{
          emptyText: submittedKeyword
            ? `'${submittedKeyword}' 에 해당하는 구성원이 없습니다.`
            : '등록된 구성원이 없습니다.',
        }}
      />

      <UserFormModal
        open={formOpen}
        target={editTarget}
        onClose={() => {
          setFormOpen(false);
          setEditTarget(null);
        }}
      />

      <MemberBulkModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => invalidate()}
      />

      <UserAccessRuleModal
        open={Boolean(accessTarget)}
        target={accessTarget}
        onClose={() => setAccessTarget(null)}
      />

      <UserImageModal
        open={imageTarget !== null}
        userId={imageTarget?.userId ?? null}
        userName={imageTarget?.name ?? ''}
        onClose={() => setImageTarget(null)}
      />
    </Space>
  );
}
